import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { filterItemMastersByCompanySource } from 'components/fbm/substitutions/substitutionExternalSkuOptions';
import {
    DEFAULT_MAX_OPTIONS_RENDERED,
    filterAndLimitOptions,
    groupItemInfoNamesByExternalSku,
    normalizeSearchText,
} from 'components/procurement/components/purchaseOrdersV2/utils/itemLookupUtils';
import { Combobox } from 'shadcn/components/ui/Combobox';
import { useCompanySourcesQuery } from 'shared/hooks/inventory/useCompanySourcesQuery';
import { useDebouncedValue } from 'shared/hooks/useDebouncedValue';
import { useItemMasterQuery } from 'shared/hooks/warehouse/useItemMasterQuery';

const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';

/**
 * External SKU picker for substitution rule forms inside dialogs.
 * Mirrors PO v2 ExternalSkuPicker behavior but portals combobox content into the parent dialog.
 *
 * @param {Object} props
 * @param {number|string|null} [props.companySource=null] - Company source id, code, or display name. Resolved against the company sources API. Omit to list every item master.
 * @param {number} [props.debounceMs=150]
 * @param {boolean} [props.enabled=true]
 * @param {string} [props.label='External SKU']
 * @param {number} [props.maxOptionsRendered]
 * @param {(value: string) => void} props.onValueChange
 * @param {HTMLElement|null} [props.popoverContainer=null]
 * @param {boolean} [props.showLabel=true]
 * @param {string} [props.value='']
 */
export function SubstitutionExternalSkuPicker({
    companySource = null,
    debounceMs = 150,
    enabled = true,
    label = 'External SKU',
    maxOptionsRendered = DEFAULT_MAX_OPTIONS_RENDERED,
    onValueChange,
    popoverContainer = null,
    showLabel = true,
    value = '',
}) {
    const labelId = useId();
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebouncedValue(searchTerm, debounceMs);
    const activeSearch = searchTerm === '' ? '' : debouncedSearch;

    const prevValueRef = useRef(String(value ?? ''));
    useEffect(() => {
        const nextValue = String(value ?? '');
        const prevValue = prevValueRef.current;
        prevValueRef.current = nextValue;
        if (!nextValue && prevValue && searchTerm) {
            setSearchTerm('');
        }
    }, [searchTerm, value]);

    const handleSelect = useCallback(
        (nextValue) => {
            setSearchTerm('');
            onValueChange(String(nextValue ?? ''));
        },
        [onValueChange],
    );

    const hasCompanySource = companySource != null && String(companySource).trim() !== '';

    const {
        data: itemMasters = [],
        isFetching,
        isLoading,
    } = useItemMasterQuery({
        activeOnly: true,
        enabled,
        mmOnly: false,
    });

    const {
        data: companySources = [],
        isFetching: isFetchingCompanySources,
        isLoading: isLoadingCompanySources,
    } = useCompanySourcesQuery({
        enabled: enabled && hasCompanySource,
    });

    const baseOptions = useMemo(() => {
        const mappedItemMasters = filterItemMastersByCompanySource(itemMasters, companySource, companySources).map(
            (row) => ({
                externalSku: row.externalSku,
                name: row.itemNameInternal,
            }),
        );
        const grouped = groupItemInfoNamesByExternalSku(mappedItemMasters);

        const options = Array.from(grouped.entries()).map(([externalSku, namesSet]) => {
            const names = Array.from(namesSet).filter(Boolean);
            names.sort((a, b) => a.localeCompare(b));
            const primaryName = names[0] ?? '';
            const extraCount = Math.max(0, names.length - 1);
            const displayLabel =
                extraCount > 0
                    ? `SKU ${externalSku}: ${primaryName} (and ${extraCount} more)`
                    : `SKU ${externalSku}: ${primaryName}`;
            const searchText = normalizeSearchText(`${externalSku} ${names.join(' ')}`);
            return { label: displayLabel, searchText, value: externalSku };
        });

        options.sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true }));
        return options;
    }, [companySource, companySources, itemMasters]);

    const options = useMemo(() => {
        const selectedValue = String(value ?? '');
        const selectedOption =
            selectedValue && baseOptions.length ? baseOptions.find((option) => option.value === selectedValue) : null;
        const fallbackSelectedOption =
            selectedValue && !selectedOption ? { label: `SKU ${selectedValue}`, value: selectedValue } : null;
        const limitedOptions = filterAndLimitOptions(baseOptions, activeSearch, maxOptionsRendered).map(
            ({ label: optLabel, value: optValue }) => ({
                label: optLabel,
                value: optValue,
            }),
        );
        if (!selectedValue) return limitedOptions;
        if (limitedOptions.some((option) => option.value === selectedValue)) {
            return limitedOptions;
        }
        if (selectedOption) {
            return [selectedOption, ...limitedOptions];
        }
        if (fallbackSelectedOption) {
            return [fallbackSelectedOption, ...limitedOptions];
        }
        return limitedOptions;
    }, [activeSearch, baseOptions, maxOptionsRendered, value]);

    const showLoading =
        isLoading ||
        (hasCompanySource && (isLoadingCompanySources || (isFetchingCompanySources && companySources.length === 0))) ||
        (isFetching && baseOptions.length === 0);
    const isComboboxDisabled = baseOptions.length === 0;

    if (showLoading) {
        return (
            <div className="flex flex-col gap-2">
                {showLabel && label && (
                    <span id={labelId} className={labelClassName}>
                        {label}
                    </span>
                )}
                <div className="flex h-10 w-full items-center justify-center gap-2 rounded-md border border-input bg-card px-3 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    <span className="text-sm">Loading external skus…</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            {showLabel && label ? (
                <span id={labelId} className={labelClassName}>
                    {label}
                </span>
            ) : null}
            {!showLabel && label ? (
                <span id={labelId} className="sr-only">
                    {label}
                </span>
            ) : null}
            <Combobox
                id={labelId}
                options={options}
                value={String(value ?? '')}
                handleSelect={handleSelect}
                placeholder="Select external sku"
                disabled={isComboboxDisabled}
                popoverWidth="trigger"
                className="z-[2100]"
                container={popoverContainer}
                triggerClassName="w-full"
                truncateLabel
                onSearchValueChange={setSearchTerm}
                commandProps={{
                    filter: () => 1,
                }}
            />
        </div>
    );
}

SubstitutionExternalSkuPicker.displayName = 'SubstitutionExternalSkuPicker';
